import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/auth/auth-context";
import { VerifyEmailBanner } from "@/auth/verify-email-banner";
import { Wordmark } from "@/components/logo";
import { Button, cx } from "@/components/ui";
import { Avatar } from "@/components/avatar";
import {
  HomeIcon,
  TrainIcon,
  LibraryIcon,
  CreateIcon,
  ProjectsIcon,
  CommunityIcon,
  StatsIcon,
  SettingsIcon,
  CalendarIcon,
  AdminIcon,
  type NavIconComponent,
} from "@/components/nav-icons";

type NavItem = { to: string; label: string; end?: boolean; Icon: NavIconComponent };

const NAV: NavItem[] = [
  { to: "/app", label: "Home", end: true, Icon: HomeIcon },
  { to: "/app/train", label: "Train", Icon: TrainIcon },
  { to: "/app/library", label: "Library", Icon: LibraryIcon },
  { to: "/app/create", label: "Create", Icon: CreateIcon },
  { to: "/app/projects", label: "Projects", Icon: ProjectsIcon },
  { to: "/app/community", label: "Community", Icon: CommunityIcon },
  { to: "/app/calendar", label: "Calendar", Icon: CalendarIcon },
  { to: "/app/stats", label: "Stats", Icon: StatsIcon },
  { to: "/app/settings", label: "Settings", Icon: SettingsIcon },
];

// Admin-only operator console — appended to the nav when the user is an admin.
const ADMIN_NAV: NavItem = { to: "/app/admin", label: "Admin", Icon: AdminIcon };

export function AppShell() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const onLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  const displayName = user?.display_name || user?.username || user?.email || t("Learner");
  const nav = user?.is_admin ? [...NAV, ADMIN_NAV] : NAV;

  return (
    <div className="flex min-h-[100dvh] flex-col overflow-x-clip bg-brand-50/30">
      <header className="sticky top-0 z-20 border-b border-black/5 bg-white/85 backdrop-blur">
        <div className="container-page flex h-16 items-center gap-4">
          <NavLink to="/app" className="shrink-0 text-ink">
            <Wordmark className="text-lg" />
          </NavLink>
          {/* Desktop nav: icon + label. Labels reveal at xl so the 8-item bar
              doesn't overflow on md/lg (those show compact icon-only + tooltip). */}
          <nav className="hidden flex-1 items-center gap-0.5 md:flex">
            {nav.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                title={t(item.label)}
                className={({ isActive }) =>
                  cx(
                    "flex items-center gap-2 rounded-lg px-2.5 py-2 text-sm font-medium transition-colors",
                    isActive
                      ? "bg-brand-100 text-brand-700"
                      : "text-ink-soft hover:bg-black/5 hover:text-ink",
                  )
                }
              >
                <item.Icon className="h-5 w-5 shrink-0" />
                <span className="hidden xl:inline">{t(item.label)}</span>
              </NavLink>
            ))}
          </nav>
          <div className="ml-auto flex items-center gap-3">
            <NavLink to="/app/profile" className="flex items-center gap-2" title={displayName}>
              <Avatar url={user?.avatar_url} name={displayName} size={32} />
              <span className="hidden text-sm text-ink-faint sm:inline">{displayName}</span>
            </NavLink>
            <Button variant="ghost" size="sm" onClick={onLogout}>
              {t("Sign out")}
            </Button>
          </div>
        </div>
      </header>

      <VerifyEmailBanner />

      <main className="container-page flex-1 py-6 pb-24 sm:py-8 md:pb-8">
        <Outlet />
      </main>

      {/* Mobile bottom tab bar — fixed, all 8 tabs (icon over label), safe-area
          aware. Replaces the old horizontal-scroll row that ran off-screen. */}
      <nav className="fixed inset-x-0 bottom-0 z-30 border-t border-black/5 bg-white/95 pb-[env(safe-area-inset-bottom)] backdrop-blur md:hidden">
        <div
          className="grid"
          style={{ gridTemplateColumns: `repeat(${nav.length}, minmax(0, 1fr))` }}
        >
          {nav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                cx(
                  "flex flex-col items-center justify-center gap-0.5 py-2 text-[10px] font-medium leading-none transition-colors",
                  isActive ? "text-brand-700" : "text-ink-faint",
                )
              }
            >
              <item.Icon className="h-5 w-5" />
              <span className="truncate">{t(item.label)}</span>
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}
